import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, ButtonGroup, Card } from '@/components/ui';
import { cn } from '@/lib/cn';
import { assetsApi } from '@/lib/api/assets';
import { useI18n } from '@/i18n';
import type { AssetItem } from '@/types/assets';

const ALL_FILTER = 'all';

export function AssetListTab() {
  const { t } = useI18n();
  const [filter, setFilter] = useState(ALL_FILTER);
  const { data, error, isLoading } = useQuery({
    queryKey: ['assets', 'items'],
    queryFn: () => assetsApi.getItems(undefined),
  });
  const items = useMemo(() => data ?? [], [data]);
  const filterOptions = useMemo(() => {
    const assetTypes = Array.from(new Set(items.map((asset) => asset.asset_type).filter(Boolean))).sort((a, b) =>
      a.localeCompare(b)
    );
    return [ALL_FILTER, ...assetTypes];
  }, [items]);
  const visibleItems = useMemo(
    () => (filter === ALL_FILTER ? items : items.filter((asset) => asset.asset_type === filter)),
    [filter, items]
  );

  useEffect(() => {
    if (!filterOptions.includes(filter)) {
      setFilter(ALL_FILTER);
    }
  }, [filter, filterOptions]);

  if (isLoading) {
    return <div className="p-6 text-muted-foreground">{t('common.loading')}</div>;
  }

  if (error) {
    return <div className="p-6 text-destructive">{t('assetsPage.loadFailed')}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="font-medium text-foreground">{t('assetsPage.tableTitle')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('assetsPage.itemCount', { count: String(visibleItems.length) })}
          </p>
        </div>
        <ButtonGroup
          size="sm"
          value={filter}
          onChange={setFilter}
          options={filterOptions.map((value) => ({
            value,
            label: value === ALL_FILTER ? t('assetsPage.allTypes') : value,
          }))}
          className="flex-wrap"
        />
      </div>

      <Card padding={false} className="overflow-x-auto">
        <table className="w-full min-w-[1100px] text-sm">
          <thead>
            <tr className="bg-muted text-left text-muted-foreground">
              <th className="p-2">{t('assetsPage.asset')}</th>
              <th className="p-2">{t('assetsPage.type')}</th>
              <th className="p-2">{t('assetsPage.provider')}</th>
              <th className="p-2">{t('assetsPage.environment')}</th>
              <th className="p-2">{t('assetsPage.expiry')}</th>
              <th className="p-2">{t('assetsPage.owner')}</th>
              <th className="p-2">{t('assetsPage.location')}</th>
              <th className="p-2">{t('assetsPage.rotation')}</th>
              <th className="p-2">{t('assetsPage.notes')}</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.length === 0 ? (
              <tr>
                <td className="p-6 text-center text-muted-foreground" colSpan={9}>
                  {t('assetsPage.empty')}
                </td>
              </tr>
            ) : (
              visibleItems.map((asset) => (
                <AssetRow key={`${asset.source_row}-${asset.asset_type}-${asset.name}`} asset={asset} />
              ))
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function AssetRow({ asset }: { asset: AssetItem }) {
  return (
    <tr className="border-t border-border align-top">
      <td className="p-2 min-w-56">
        <div className="font-medium text-foreground">{asset.name}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">#{asset.source_row}</div>
        {asset.identifier && <div className="mt-1 break-all text-xs text-muted-foreground">{asset.identifier}</div>}
      </td>
      <td className="p-2 whitespace-nowrap text-foreground">{asset.asset_type}</td>
      <td className="p-2 whitespace-nowrap text-foreground">{asset.provider ?? '-'}</td>
      <td className="p-2 whitespace-nowrap text-foreground">{asset.environment ?? '-'}</td>
      <td className="p-2 whitespace-nowrap">
        <ExpiryBadge expiryDate={asset.expiry_date} />
      </td>
      <td className="p-2 whitespace-nowrap text-foreground">{asset.owner ?? '-'}</td>
      <td className="p-2 min-w-44 text-foreground">
        <TruncatedText value={asset.location} />
      </td>
      <td className="p-2 whitespace-nowrap text-foreground">{asset.rotation_last ?? '-'}</td>
      <td className="p-2 min-w-56 text-muted-foreground">
        <TruncatedText value={asset.notes} />
      </td>
    </tr>
  );
}

function ExpiryBadge({ expiryDate }: { expiryDate: string | null }) {
  const { t } = useI18n();

  if (!expiryDate) {
    return (
      <Badge variant="outline">
        {t('assetsPage.noExpiry')}
      </Badge>
    );
  }

  const days = daysUntil(expiryDate);
  const expired = days < 0;
  const dueSoon = days <= 30 && !expired;
  const variant = expired ? 'destructive' : dueSoon ? 'warning' : 'success';

  return (
    <div className="flex flex-col gap-1">
      <span className="text-foreground">{expiryDate}</span>
      <Badge variant={variant} className="w-fit">
        {expired ? t('assetsPage.expired') : dueSoon ? t('assetsPage.dueSoon') : t('assetsPage.normal')} ({days}d)
      </Badge>
    </div>
  );
}

function TruncatedText({ value }: { value: string | null }) {
  if (!value) {
    return <span>-</span>;
  }

  return (
    <span className={cn('line-clamp-2 break-words')} title={value}>
      {value}
    </span>
  );
}

function daysUntil(dateString: string): number {
  const [year, month, day] = dateString.split('-').map(Number);
  const target = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - today.getTime()) / 86400000);
}
